import { GAME_CONFIG } from '../config.ts';
import { Language } from '../core/Storage.ts';

export class Player {
  public x: number;
  public y: number;
  public width: number;
  public height: number;
  public boostTimeLeft: number = 0;
  public boostLabel: string = '⚡ ТУРБО';
  private virtualWidth: number;
  private glowHue: number = 0;
  private tiltAngle: number = 0;
  private prevX: number;

  constructor(virtualWidth: number, virtualHeight: number) {
    this.virtualWidth = virtualWidth;
    this.width = GAME_CONFIG.trayWidth;
    this.height = GAME_CONFIG.trayHeight;
    this.x = virtualWidth / 2;
    this.prevX = this.x;
    this.y = virtualHeight - GAME_CONFIG.trayYOffset;
  }

  public reset(virtualWidth: number, virtualHeight: number): void {
    this.virtualWidth = virtualWidth;
    this.x = virtualWidth / 2;
    this.prevX = this.x;
    this.y = virtualHeight - GAME_CONFIG.trayYOffset;
    this.boostTimeLeft = 0;
    this.tiltAngle = 0;
  }

  public activateBoost(duration: number): void {
    this.boostTimeLeft = Math.max(this.boostTimeLeft, duration);
  }

  public setLanguage(lang: Language): void {
    this.boostLabel = lang === 'ru' ? '⚡ ТУРБО' : '⚡ TURBO';
  }

  public update(dt: number, targetX: number | null, moveLeft: boolean, moveRight: boolean): void {
    const isBoosted = this.boostTimeLeft > 0;
    if (isBoosted) {
      this.boostTimeLeft -= dt;
      this.glowHue = (this.glowHue + dt * 360) % 360;
    }

    const currentSpeed = isBoosted
      ? GAME_CONFIG.playerBaseSpeed * GAME_CONFIG.playerBoostMultiplier
      : GAME_CONFIG.playerBaseSpeed;

    this.prevX = this.x;

    // Responsive pointer lerping
    if (targetX !== null) {
      const dx = targetX - this.x;
      // High responsiveness so it feels 1:1 on touch while still smooth
      const lerpFactor = isBoosted ? 45 : 36;
      this.x += dx * Math.min(1.0, lerpFactor * dt);
    } else {
      // Keyboard input
      let dir = 0;
      if (moveLeft) dir -= 1;
      if (moveRight) dir += 1;
      this.x += dir * currentSpeed * dt;
    }

    // Clamp inside virtual canvas boundaries
    const halfWidth = this.width / 2;
    if (this.x - halfWidth < 8) {
      this.x = 8 + halfWidth;
    } else if (this.x + halfWidth > this.virtualWidth - 8) {
      this.x = this.virtualWidth - 8 - halfWidth;
    }

    // Dynamic tilt angle based on horizontal velocity
    const velocityX = (this.x - this.prevX) / (dt || 0.016);
    const targetTilt = Math.max(-0.14, Math.min(0.14, (velocityX / 1200) * 0.14));
    this.tiltAngle += (targetTilt - this.tiltAngle) * Math.min(1.0, 18 * dt);
  }

  public draw(ctx: CanvasRenderingContext2D): void {
    ctx.save();
    ctx.translate(this.x, this.y);
    ctx.rotate(this.tiltAngle);

    const isBoosted = this.boostTimeLeft > 0;

    // Turbo speed trail when boosted
    if (isBoosted) {
      ctx.shadowColor = `hsl(${this.glowHue}, 100%, 65%)`;
      ctx.shadowBlur = 24;
      ctx.strokeStyle = `hsl(${this.glowHue}, 100%, 75%)`;
      ctx.lineWidth = 4;
      ctx.strokeRect(-this.width / 2 - 3, -this.height / 2 - 3, this.width + 6, this.height + 6);
    } else {
      ctx.shadowColor = 'rgba(255, 0, 128, 0.45)';
      ctx.shadowBlur = 12;
    }

    // 1. Waiter's White Cuffs & Hands holding the tray
    ctx.fillStyle = '#ffffff';
    ctx.beginPath();
    ctx.roundRect(-this.width * 0.38, this.height * 0.3, 24, 16, [4, 4, 8, 8]);
    ctx.roundRect(this.width * 0.38 - 24, this.height * 0.3, 24, 16, [4, 4, 8, 8]);
    ctx.fill();

    // Black tuxedo sleeves
    ctx.fillStyle = '#111118';
    ctx.beginPath();
    ctx.roundRect(-this.width * 0.40, this.height * 0.6, 28, 20, [3, 3, 6, 6]);
    ctx.roundRect(this.width * 0.40 - 28, this.height * 0.6, 28, 20, [3, 3, 6, 6]);
    ctx.fill();

    // 2. Tray Plate - Shiny Metallic Gradient
    const trayGrad = ctx.createLinearGradient(0, -this.height, 0, this.height);
    trayGrad.addColorStop(0, '#ffffff');
    trayGrad.addColorStop(0.3, '#e8ebed');
    trayGrad.addColorStop(0.7, '#a6b0b8');
    trayGrad.addColorStop(1, '#697279');

    ctx.fillStyle = trayGrad;
    ctx.beginPath();
    ctx.ellipse(0, 0, this.width / 2, this.height / 2, 0, 0, Math.PI * 2);
    ctx.fill();

    // 3. Golden Rim
    ctx.strokeStyle = isBoosted ? `hsl(${this.glowHue}, 100%, 50%)` : '#d4af37';
    ctx.lineWidth = 3.5;
    ctx.stroke();

    // 4. Inner Platter Specular Highlight
    ctx.fillStyle = 'rgba(255, 255, 255, 0.6)';
    ctx.beginPath();
    ctx.ellipse(0, -3, this.width * 0.36, this.height * 0.22, 0, 0, Math.PI * 2);
    ctx.fill();

    // 5. Boost indicator badge on the tray
    if (isBoosted) {
      ctx.fillStyle = '#ffffff';
      ctx.font = 'bold 11px system-ui, sans-serif';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText(`${this.boostLabel} ${this.boostTimeLeft.toFixed(1)}s`, 0, -18);
    }

    ctx.restore();
  }
}
